import { TaskConfigFunctionName, TaskConfigParamOptions } from "@eave/shared/types/api/tasks";
import stateOptions from "web_modules/forms/data/stateOptions";

const {
  // boolean
  isRESPA,
  initialLEBeforeRateLock,
  isJumbo,
  isVA,
  hasGiftFunds,
  hasOpenLiabilities,
  hasREOs,
  isCondo,
  borrowerAuthSigned,
  hasProfitAndLossStatementOrBalanceSheet,
  hasAppraisalRequestedDate,
  hasAppraisalExpectedDate,
  isCondoOrPUD,
  conformingOrHighBalance,
  loanProductCIC,

  // Date
  applicationDateAfter,
  borrowerAuthAfter,

  // complicated date
  dateComparison,

  // enum
  hasDisclosure,
  hasDocument,
  hasEvent,
  doesNotHaveEvent,
  isCashCloseType,
  isCashOfferInStage,
  isTradeInInStage,
  loanStatusIsOrHasBeen,
  loanDecisionIsOrHasBeen,

  // Text
  isInState,

  // number
  ltvComparison,
  loanAmountComparison,
} = TaskConfigFunctionName;

export enum TaskConfigFilterType {
  BOOL = "Booleans",
  DATE = "Date",
  DATE_COMPARISON = "Date Comparisons",
  TEXT = "Text",
  AMOUNT = "Numerical Amounts",
  CURRENCY = "Currency",
  ENUM = "Enums",
}

export interface TaskConfigSchemaDef {
  func_name: string;
  displayText: string;
  comparisonKey: string;
  type: TaskConfigFilterType;
  options: any;
}

export const defValue = (
  func_name: string,
  displayText: string,
  comparisonKey: string = null, // Most fields do not have a comparison key
  type: TaskConfigFilterType = TaskConfigFilterType.BOOL, // Most fields are bool, so let's just set default
  options: any = [],
): TaskConfigSchemaDef => ({
  func_name,
  displayText,
  comparisonKey,
  type,
  options,
});

export const getDefByKey = (schema, key: string): TaskConfigSchemaDef => {
  return schema.find(schemaObj => schemaObj.func_name === key);
};

export const getSchema = (taskOptions: TaskConfigParamOptions) => [
  defValue(isRESPA, "Is RESPA"),
  defValue(initialLEBeforeRateLock, "Initial LE is before Rate Lock"),
  defValue(isJumbo, "Is jumbo"),
  defValue(isVA, "Is VA loan"),
  defValue(hasGiftFunds, "Has gift funds"),
  defValue(hasOpenLiabilities, "Has open liabilities"),
  defValue(hasREOs, "Has REOs"),
  defValue(isCondo, "Is Condo"),
  defValue(borrowerAuthSigned, "Borrower auth is signed"),
  defValue(
    hasProfitAndLossStatementOrBalanceSheet,
    "Has either profit/loss statement or balance sheet",
  ),
  defValue(hasAppraisalRequestedDate, "Has an appraisal requested date"),
  defValue(hasAppraisalExpectedDate, "Has an appraisal expected date"),
  defValue(isCondoOrPUD, "Is either a Condo or PUD"),
  defValue(conformingOrHighBalance, "Is either conforming or high-balance"),
  defValue(applicationDateAfter, "Application date is after", "date", TaskConfigFilterType.DATE),
  defValue(borrowerAuthAfter, "Borrower auth date is after", "date", TaskConfigFilterType.DATE),
  defValue(
    hasDisclosure,
    "Has disclosure of type",
    "disclosure_type",
    TaskConfigFilterType.ENUM,
    taskOptions.disclosure_stages,
  ),
  defValue(
    hasDocument,
    "Has document of type",
    "document_type",
    TaskConfigFilterType.ENUM,
    taskOptions.document_types,
  ),
  defValue(
    hasEvent,
    "Has event of type",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.actionable_events,
  ),
  defValue(
    doesNotHaveEvent,
    "Does not have event of type",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.actionable_events,
  ),
  defValue(
    isCashCloseType,
    "Is Cash Close type",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.cash_close_types,
  ),
  defValue(
    isCashOfferInStage,
    "Is Cash Offer in stage",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.cash_offer_stages,
  ),
  defValue(
    isTradeInInStage,
    "Is Trade-In in stage",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.trade_in_stages,
  ),
  defValue(
    loanStatusIsOrHasBeen,
    "Loan status is/has been",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.loan_statuses,
  ),
  defValue(
    loanDecisionIsOrHasBeen,
    "Loan decision is/has been",
    "string",
    TaskConfigFilterType.ENUM,
    taskOptions.loan_decisions,
  ),
  defValue(
    isInState,
    "Is in state",
    "string",
    TaskConfigFilterType.ENUM,
    stateOptions.map(s => s.value),
  ),
  defValue(ltvComparison, "LTV Comparison", "amount", TaskConfigFilterType.AMOUNT),
  defValue(
    dateComparison,
    "Date Comparison",
    "dateComparison",
    TaskConfigFilterType.DATE_COMPARISON,
  ),
  defValue(loanAmountComparison, "Loan Amount Comparison", "amount", TaskConfigFilterType.CURRENCY),
  defValue(loanProductCIC, "Loan product change in circumstance"),
];
